import {
  ChainIdLegacy,
  DEFAULT_CHAIN_ID,
  SupportedChainId,
  SupportedChainValue,
} from "./chain-legacy";
import { protocolKeys } from "./protocol";
import { identity, pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { either, record } from "fp-ts";
import { isEnvVarOn } from "./env";
import { isUndefined } from "effect/Predicate";
import { ChecksumAddress, getChecksumAddressOrThrow } from "./checksum-address";
import { satisfies } from "effect/Function";

export const PROTOCOL_INSTANCES = [
  "mainnetLegacy",
  "mainnetLlama",
  "mainnetGrand",
  "arbitrum",
  "optimism",
  "base",
  "lrtbase",
  "local",
  "anvil",
] as const;

/**
 * This will cause a compilation error if the legacy protocol instance type
 * differs from the new protocol key type.
 */
export const PROTOCOL_KEY_GUARD: typeof protocolKeys = PROTOCOL_INSTANCES;

// TODO: make this typesafe with PROTOCOL_INSTANCE_CHAIN const
export const PROTOCOL_INSTANCE_PRIORITY: SupportedChainValue<
  [ProtocolInstance, ...ProtocolInstance[]]
> = {
  [ChainIdLegacy.ARBITRUM]: ["arbitrum"],
  [ChainIdLegacy.MAINNET]: ["mainnetGrand", "mainnetLegacy", "mainnetLlama"],
  [ChainIdLegacy.OPTIMISM]: ["optimism"],
  [ChainIdLegacy.BASE]: ["base", "lrtbase"],
  [ChainIdLegacy.LOCAL]: ["local"],
  [ChainIdLegacy.ANVIL]: ["anvil"],
};

/**
 * @deprecated use `ProtocolKey` instead
 */
export type ProtocolInstance = (typeof PROTOCOL_INSTANCES)[number];
export type ProtocolInstanceValue<T> = Record<ProtocolInstance, T>;

export const isProtocolInstance = (value: unknown): value is ProtocolInstance =>
  PROTOCOL_INSTANCES.some((protocolInstance) => protocolInstance === value);

export const ProtocolInstance = new t.Type<ProtocolInstance, string, unknown>(
  "ProtocolInstance",
  isProtocolInstance,
  (u, c) => {
    return pipe(
      t.string.validate(u, c),
      either.chain((str) => {
        try {
          if (isProtocolInstance(str)) {
            return t.success(str);
          }

          return t.failure(u, c);
        } catch (error) {
          return t.failure(u, c);
        }
      })
    );
  },
  identity
);

export const PROTOCOL_INSTANCE_CHAIN = satisfies<
  ProtocolInstanceValue<SupportedChainId>
>()({
  mainnetLegacy: ChainIdLegacy.MAINNET,
  mainnetLlama: ChainIdLegacy.MAINNET,
  mainnetGrand: ChainIdLegacy.MAINNET,
  arbitrum: ChainIdLegacy.ARBITRUM,
  optimism: ChainIdLegacy.OPTIMISM,
  base: ChainIdLegacy.BASE,
  lrtbase: ChainIdLegacy.BASE,
  local: ChainIdLegacy.LOCAL,
  anvil: ChainIdLegacy.ANVIL,
} as const);

export const RUNTIME_PROTOCOL_INSTANCES: ProtocolInstance[] = (
  [
    "arbitrum",
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_OPTIMISM)
      ? "optimism"
      : undefined,
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_BASE) ? "base" : undefined,
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_LRTBASE)
      ? "lrtbase"
      : undefined,
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_MAINNET_GRAND)
      ? "mainnetGrand"
      : undefined,
    "mainnetLegacy",
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_MAINNET_LLAMA)
      ? "mainnetLlama"
      : undefined,
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_LOCAL)
      ? "local"
      : undefined,
    isEnvVarOn(() => process.env.NEXT_PUBLIC_SUPPORT_ANVIL)
      ? "anvil"
      : undefined,
  ] as const
).flatMap((protocolInstance) =>
  isUndefined(protocolInstance) ? [] : [protocolInstance]
);

export const isRuntimeProtocolInstance = (
  protocolInstance?: ProtocolInstance
): protocolInstance is ProtocolInstance => {
  if (isUndefined(protocolInstance)) {
    return false;
  }

  return RUNTIME_PROTOCOL_INSTANCES.includes(protocolInstance);
};

/**
 * Creates a protocol instance value getter that returns a default value in the
 * case of an undefined protocol instance. Values for all protocol instances
 * must be specified.
 */
export const createProtocolInstanceValueGetter =
  <T>(defaultValue: T, protocolInstanceValues: ProtocolInstanceValue<T>) =>
  (protocolInstance?: ProtocolInstance): T => {
    if (isUndefined(protocolInstance)) return defaultValue;

    return protocolInstanceValues[protocolInstance];
  };

export const getProtocolInstanceChainId = createProtocolInstanceValueGetter(
  DEFAULT_CHAIN_ID,
  PROTOCOL_INSTANCE_CHAIN
);

type FeatureKeys = "xai" | "graph" | "analytics";
type FeatureConfigGuard = ProtocolInstanceValue<Record<FeatureKeys, boolean>>;

const featureConfig = satisfies<FeatureConfigGuard>()({
  mainnetLegacy: {
    xai: true,
    graph: true,
    analytics: true,
  },
  mainnetLlama: {
    xai: false,
    graph: true,
    analytics: true,
  },
  mainnetGrand: {
    xai: false,
    graph: true,
    analytics: false,
  },
  local: {
    xai: false,
    graph: false,
    analytics: false,
  },
  arbitrum: {
    xai: false,
    graph: true,
    analytics: true,
  },
  optimism: {
    xai: false,
    graph: true,
    analytics: true,
  },
  base: {
    xai: false,
    graph: true,
    analytics: false,
  },
  lrtbase: {
    xai: false,
    graph: true,
    analytics: false,
  },
  anvil: {
    xai: true,
    graph: false,
    analytics: false,
  },
} as const);

type FeatureConfig = typeof featureConfig;

export type FeatureProtocolInstance<F extends FeatureKeys> = {
  [K in keyof FeatureConfig]: FeatureConfig[K][F] extends true ? K : never;
}[keyof FeatureConfig];

export type FeatureProtocolInstanceValue<F extends FeatureKeys, T> = Record<
  FeatureProtocolInstance<F>,
  T
>;

export const isFeatureProtocolInstance =
  <F extends FeatureKeys>(feature: F) =>
  (
    protocolInstance?: ProtocolInstance
  ): protocolInstance is FeatureProtocolInstance<F> => {
    if (isUndefined(protocolInstance)) return false;

    return featureConfig[protocolInstance][feature];
  };

export const isRuntimeFeatureProtocolInstance =
  <F extends FeatureKeys>(feature: F) =>
  (
    protocolInstance?: ProtocolInstance
  ): protocolInstance is FeatureProtocolInstance<F> =>
    isFeatureProtocolInstance(feature)(protocolInstance) &&
    isRuntimeProtocolInstance(protocolInstance);

export const createFeatureProtocolInstanceValueGetter =
  <F extends FeatureKeys, T>(value: FeatureProtocolInstanceValue<F, T>) =>
  (chainId: FeatureProtocolInstance<F>): T =>
    value[chainId];

/**
 * For a given feature (`'xai'`, `'graph'`, etc.), returns an array of all the
 * protocol instances that are supported for that feature AND runtime supported.
 */
export const getRuntimeFeatureProtocolInstances = <F extends FeatureKeys>(
  feature: F
): FeatureProtocolInstance<F>[] =>
  PROTOCOL_INSTANCES.filter(isRuntimeFeatureProtocolInstance(feature));

export type GraphProtocolInstance = FeatureProtocolInstance<"graph">;

export const createChecksumAddressProtocolInstanceValueOrThrow = (
  checksumAddressProtocolInstanceValue: ProtocolInstanceValue<string>
): ProtocolInstanceValue<ChecksumAddress> =>
  pipe(
    checksumAddressProtocolInstanceValue,
    record.map(getChecksumAddressOrThrow)
  );

/**
 * Map of URL-friendly strings that represent protocol instances.
 */
const PROTOCOL_INSTANCE_AS_URL: ProtocolInstanceValue<string> = {
  mainnetLegacy: "ethereum-legacy",
  mainnetLlama: "llama-edition",
  mainnetGrand: "ethereum",
  arbitrum: "arbitrum",
  optimism: "optimism",
  base: "base",
  lrtbase: "btcfi",
  local: "local",
  anvil: "anvil",
};

export const ProtocolInstanceAsUrl = new t.Type<
  ProtocolInstance,
  string,
  unknown
>(
  "ProtocolInstanceAsUrl",
  isProtocolInstance,
  (u, c) => {
    return pipe(
      t.string.validate(u, c),
      either.chain((str) => {
        try {
          const protocolInstanceAsUrlEntry = record
            .toEntries(PROTOCOL_INSTANCE_AS_URL)
            .find(([, value]) => value === str);
          if (!protocolInstanceAsUrlEntry) return t.failure(u, c);

          const [protocolInstance] = protocolInstanceAsUrlEntry;
          if (isProtocolInstance(protocolInstance)) {
            return t.success(protocolInstance);
          }

          return t.failure(u, c);
        } catch (error) {
          return t.failure(u, c);
        }
      })
    );
  },
  (protocolInstance) => PROTOCOL_INSTANCE_AS_URL[protocolInstance]
);

const RUNTIME_SUPPORTED_CHAINS = [
  ...new Set(RUNTIME_PROTOCOL_INSTANCES.map(getProtocolInstanceChainId)),
];

export const isRuntimeSupportedChainId = (
  chainId: ChainIdLegacy
): chainId is SupportedChainId =>
  RUNTIME_SUPPORTED_CHAINS.some(
    (runtimeSupportedChain) => runtimeSupportedChain === chainId
  );
